/**
 * Ozi in the admin panel (Anthony, 7 October 2026): "every aspect of the admin panel should
 * be able to speak to the admin", so that a blind person can be employed in any job here.
 *
 * Ozi says what is waiting when someone signs in, reads any list out one item at a time, moves
 * between the tabs, and carries out decisions by voice after reading them back and hearing yes.
 * Kept apart from the screen, as plain functions, so every phrase can be tested on its own.
 */

import type {
  NeedsYouCard,
  OwnerMoney,
  ProblemDecision,
  StaffOverview,
  StaffArea,
  StaffDocument,
  StaffEnquiry,
  StaffFeedback,
  StaffFindRequest,
  StaffProblem,
  StaffAnalytics,
  StaffPartnerProduct,
  StaffRecovery,
  TeamMember,
  LearningRow,
  BankPaymentRow,
  CancellationsReport,
  RefundsReport,
  RunnersNow,
  StaffOrderRow,
} from '../lib/api';
import { money } from '../lib/money';
import { pairsAloud } from '../lib/phone-aloud';

/** Everything the signed-in person's job lets them see, as last loaded. */
export interface StaffLists {
  documents?: StaffDocument[];
  problems?: StaffProblem[];
  feedback?: StaffFeedback[];
  owed?: StaffRecovery[];
  finds?: StaffFindRequest[];
  enquiries?: StaffEnquiry[];
  /** Partner shop products waiting to be checked. */
  partners?: StaffPartnerProduct[];
  /** Numbers, not a list to work through. */
  analytics?: never[];
  /** What needs a person now, most urgent first (16 October 2026 review): read before the rest. */
  overview?: NeedsYouCard[];
  money?: never[];
  team?: TeamMember[];
  /** What Ozi could not answer, waiting for an answer (ruling 49). */
  learning?: LearningRow[];
  /** Bank transfers waiting to be checked against the business account (ruling 50). */
  payments?: BankPaymentRow[];
  /** Live orders, read one at a time with no Shopper's name (Section Q). */
  orders?: StaffOrderRow[];
  /** Numbers, not lists to work through. */
  runners?: never[];
  reports?: never[];
  accounts?: never[];
  audit?: never[];
  /** The company inbox (ruling 77): its own page, not a list Ozi reads here. */
  messages?: never[];
  /** Areas (30 October 2026): a screen of figures, not a list Ozi reads here. */
  areas?: never[];
  /** Referrals and Ambassadors (30 October 2026): a screen of figures, not a list Ozi reads. */
  referrals?: never[];
  /** Checkout insights (30 October 2026): a screen of figures, not a list Ozi reads. */
  insights?: never[];
  /** Ambassador applications (30 October 2026): a page of its own, not a list Ozi reads. */
  ambassadors?: never[];
}

/** Parts that are figures or screens rather than things waiting for somebody. */
const NOT_WAITING: readonly StaffArea[] = [
  'team',
  'analytics',
  'overview',
  'money',
  'orders',
  'runners',
  'reports',
  'accounts',
  'audit',
  'messages',
  'areas',
  'referrals',
  'insights',
  'ambassadors',
];

/** What each part is called aloud, as one and as many. */
export const AREA_WORDS: Record<StaffArea, { one: string; many: string; tab: string }> = {
  documents: {
    one: 'Runner document to check',
    many: 'Runner documents to check',
    tab: 'Documents',
  },
  problems: { one: 'complaint to decide', many: 'complaints to decide', tab: 'Problems' },
  feedback: { one: 'feedback message', many: 'feedback messages', tab: 'Feedback' },
  owed: { one: 'Runner who owes money', many: 'Runners who owe money', tab: 'Money owed' },
  finds: { one: 'Finds It request', many: 'Finds It requests', tab: 'Finds It' },
  enquiries: { one: 'enquiry to ring back', many: 'enquiries to ring back', tab: 'Enquiries' },
  partners: {
    one: 'shop product to check',
    many: 'shop products to check',
    tab: 'Shops and organisations',
  },
  analytics: { one: 'report', many: 'reports', tab: 'Analytics' },
  overview: { one: 'thing that needs you', many: 'things that need you', tab: 'Needs you now' },
  money: { one: 'payment', many: 'payments', tab: 'Money' },
  team: { one: 'person on the team', many: 'people on the team', tab: 'Team' },
  payments: {
    one: 'bank transfer to check',
    many: 'bank transfers to check',
    tab: 'Payments',
  },
  learning: {
    one: 'question Ozi could not answer',
    many: 'questions Ozi could not answer',
    tab: 'Learning',
  },
  orders: { one: 'live order', many: 'live orders', tab: 'Orders' },
  runners: { one: 'Runner', many: 'Runners', tab: 'Runners' },
  reports: { one: 'report', many: 'reports', tab: 'Reports' },
  accounts: { one: 'Shopper account', many: 'Shopper accounts', tab: 'Shopper accounts' },
  audit: { one: 'audit entry', many: 'audit entries', tab: 'Audit log' },
  messages: { one: 'message', many: 'messages', tab: 'Messages' },
  areas: { one: 'area', many: 'areas', tab: 'Areas' },
  referrals: {
    one: 'person who referred others',
    many: 'people who referred others',
    tab: 'Referrals',
  },
  insights: { one: 'checkout figure', many: 'checkout figures', tab: 'Checkout insights' },
  ambassadors: {
    one: 'Ambassador application',
    many: 'Ambassador applications',
    tab: 'Ambassadors',
  },
};

/** The items of one part that are still waiting for somebody. */
export function waiting(lists: StaffLists, area: StaffArea): unknown[] {
  if (area === 'enquiries') return (lists.enquiries ?? []).filter((row) => !row.handled);
  return (lists[area] as unknown[] | undefined) ?? [];
}

function greeting(now: Date): string {
  const hour = now.getHours();
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
}

/** What Ozi says when someone signs in: who they are, and what is waiting for them. */
export function summary(
  name: string,
  title: string,
  areas: readonly StaffArea[],
  lists: StaffLists,
  now = new Date(),
): string {
  const parts: string[] = [];
  for (const area of areas) {
    if (NOT_WAITING.includes(area)) continue;
    const count = waiting(lists, area).length;
    if (count === 0) continue;
    const words = AREA_WORDS[area];
    let part = `${count} ${count === 1 ? words.one : words.many}`;
    if (area === 'problems') {
      const names = (lists.problems ?? [])
        .slice(0, 3)
        .map((row) => row.reporterName ?? (row.reportedBy === 'runner' ? 'a Runner' : 'a Shopper'));
      part += `, from ${listWords(names)}${count > 3 ? ' and others' : ''}`;
      const overdue = (lists.problems ?? []).filter((row) => row.overdue).length;
      if (overdue > 0) part += `. ${overdue} ${overdue === 1 ? 'is' : 'are'} overdue`;
    }
    parts.push(part);
  }
  const opening = `${greeting(now)}, ${name}. You're signed in as ${title}.`;
  // What needs a person now comes first, in its own order, for whoever has the first screen.
  const urgent =
    areas.includes('overview') && lists.overview && lists.overview.length > 0
      ? ` ${needsYouWords(lists.overview)}`
      : '';
  if (parts.length === 0) {
    return urgent
      ? `${opening}${urgent} Say "help" to hear what I can do.`
      : `${opening} Nothing is waiting for you right now. Say "help" to hear what I can do.`;
  }
  return `${opening}${urgent} You have ${listWords(parts)}. Say, for example, "read me the ${firstNoun(areas, lists)}", or "help".`;
}

/** One, two, three, up to twelve, then figures. */
function countWords(count: number): string {
  const words = [
    'no',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
  ];
  return words[count] ?? String(count);
}

/**
 * What needs a person now, in one breath and in the server's order (an SOS first, then orders
 * waiting for a Runner, bank transfers, Runner pay-backs, problems, documents): "Two things need
 * you: one SOS, one bank transfer to check."
 */
export function needsYouWords(cards: readonly NeedsYouCard[]): string {
  if (cards.length === 0) return 'Nothing needs you right now.';
  const total = cards.reduce((sum, card) => sum + card.count, 0);
  const count = countWords(total);
  const opening =
    total === 1
      ? 'One thing needs you'
      : `${count[0]!.toUpperCase()}${count.slice(1)} things need you`;
  // A plain list, with commas: "one SOS, one bank transfer to check", as the review wrote it.
  return `${opening}: ${cards.map((card) => card.words).join(', ')}.`;
}

function firstNoun(areas: readonly StaffArea[], lists: StaffLists): string {
  const area =
    areas.find((one) => !NOT_WAITING.includes(one) && waiting(lists, one).length > 0) ??
    (areas.includes('analytics') ? 'analytics' : 'problems');
  return {
    documents: 'documents',
    problems: 'complaints',
    feedback: 'feedback',
    owed: 'money owed',
    finds: 'Finds It requests',
    enquiries: 'enquiries',
    partners: 'shop products',
    analytics: 'analytics',
    overview: 'overview',
    money: 'money',
    team: 'team',
    learning: 'learning list',
    payments: 'payments',
    orders: 'orders',
    runners: 'Runners',
    reports: 'reports',
    accounts: 'Shopper accounts',
    audit: 'audit log',
    messages: 'messages',
    areas: 'areas',
    referrals: 'referrals',
    insights: 'checkout insights',
    ambassadors: 'Ambassador applications',
  }[area];
}

export function listWords(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;
}

function longDate(when: string): string {
  return new Date(when).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** One item, read out in full. `position` is "1 of 3". */
export function readItem(area: StaffArea, item: unknown, position: string): string {
  switch (area) {
    case 'payments': {
      const row = item as BankPaymentRow;
      return `Bank transfer ${position}. ${row.shopperName}, ${money(row.amountPence)}, reference ${row.reference ?? 'none'}. When you have seen it arrive in the bank account, mark it as received in the Payments tab. Or say next.`;
    }
    case 'learning': {
      const row = item as LearningRow;
      return `Question ${position}, asked ${row.timesHeard} ${row.timesHeard === 1 ? 'time' : 'times'}: "${row.text}". To teach me the answer, type it in the Learning tab and press approve. Or say next.`;
    }
    case 'problems': {
      const row = item as StaffProblem;
      const from = row.reporterName ?? (row.reportedBy === 'runner' ? 'A Runner' : 'A Shopper');
      const role = row.reportedBy === 'runner' ? 'a Runner' : 'a Shopper';
      const count = (kind: string, one: string): string => {
        const n = row.evidence.filter((piece) => piece.kind === kind).length;
        return n === 0 ? '' : `${n} ${one}${n === 1 ? '' : 's'}`;
      };
      const evidence = [
        count('voice_note', 'voice note'),
        count('photo', 'photo'),
        count('note', 'written note'),
      ].filter(Boolean);
      const notes = row.evidence
        .filter((piece) => piece.kind === 'note' && piece.text)
        .map(
          (piece) =>
            ` A note from the ${piece.addedBy === 'runner' ? 'Runner' : 'Shopper'} says: ${piece.text}`,
        )
        .join('');
      return (
        `Complaint ${position}. ${from}, ${role}, says: ${row.summary}` +
        (row.refundRequestedPence ? ` They ask for ${money(row.refundRequestedPence)} back.` : '') +
        (evidence.length > 0 ? ` With it: ${listWords(evidence)}.` : '') +
        notes +
        (row.overdue ? ' It is overdue.' : ` Decide it by ${longDate(row.decideBy)}.`) +
        ' To decide, say, for example, "nobody at fault, refund 2 pounds 50", or say "next".'
      );
    }
    case 'feedback': {
      const row = item as StaffFeedback;
      return `Feedback ${position}, from ${row.runner ? `${row.runner.name}, a Runner` : 'someone who gave no name'}, on ${longDate(row.createdAt)}: ${row.message}`;
    }
    case 'documents': {
      const row = item as StaffDocument;
      return (
        `Document ${position}. ${row.runner?.name ?? 'A Runner'} sent their ${row.name}` +
        (row.sentAs === 'share code' && row.shareCode
          ? `, as a share code: ${row.shareCode.split('').join(' ')}. Check it on the government website first.`
          : ', as a photo.') +
        ' Say "accept" or "turn it down", or "next".'
      );
    }
    case 'owed': {
      const row = item as StaffRecovery;
      return `${position}. ${row.runner.name} owes ${money(row.remainingPence)}.${row.canWriteOff ? ' It is small enough to write off: say "write it off", or "next".' : ' It is too much to write off, so please ask them for it.'}`;
    }
    case 'finds': {
      const row = item as StaffFindRequest;
      return `Finds It ${position}. ${row.shopperName}${row.area ? `, near ${row.area},` : ''} is looking for ${row.description}. When you've looked, say "found it", or "not found", or "next".`;
    }
    case 'enquiries': {
      const row = item as StaffEnquiry;
      return (
        `Enquiry ${position}, from ${row.organisation}. Ask for ${row.contactName}, on ${pairsAloud(row.telephone)}.` +
        (row.people ? ` They support ${row.people} people.` : '') +
        (row.message ? ` They wrote: ${row.message}` : '') +
        ' When you have rung them, say "rung back".'
      );
    }
    case 'partners': {
      const row = item as StaffPartnerProduct;
      return (
        `Shop product ${position}. ${row.shopName} sent ${row.name}, at ${money(row.pricePence)}` +
        (row.tags ? `, labelled ${row.tags}` : '') +
        (row.expiresOn ? `, best before ${longDate(row.expiresOn)}` : '') +
        `. ${row.hasPhoto ? 'It has a photo.' : 'It has no photo.'} Say "accept" or "turn it down", or "next".`
      );
    }
    case 'orders': {
      // No Shopper's name aloud: the screen has it, for whoever needs it.
      const row = item as StaffOrderRow;
      return `Order ${position}, ${row.reference.split('').join(' ')}: ${row.statusWords}. ${row.itemCount} ${row.itemCount === 1 ? 'item' : 'items'}${row.area ? `, to ${row.area.split('').join(' ')}` : ''}${row.runnerName ? `, with ${row.runnerName}` : ''}. Say "next", or open it on the screen for its timeline.`;
    }
    case 'analytics':
    case 'overview':
    case 'money':
    case 'runners':
    case 'reports':
    case 'accounts':
    case 'audit':
    case 'messages':
    case 'areas':
    case 'referrals':
    case 'insights':
    case 'ambassadors':
      return '';
    case 'team': {
      const row = item as TeamMember;
      return `${position}. ${row.name}, ${row.title}, signs in as ${row.username}${row.active ? '' : '. Their account is turned off'}.`;
    }
  }
}

/** What someone said, understood. */
export type StaffCommand =
  | { kind: 'summary' }
  | { kind: 'help' }
  | { kind: 'open'; area: StaffArea }
  | { kind: 'next' }
  | { kind: 'previous' }
  | { kind: 'again' }
  | { kind: 'decide'; decision: ProblemDecision; refundPence: number | null }
  | { kind: 'accept' }
  | { kind: 'reject' }
  | { kind: 'write-off' }
  | { kind: 'not-found' }
  | { kind: 'found' }
  | { kind: 'rung-back' }
  | { kind: 'sign-out' }
  /** "How many signups this month?" (Section Q). */
  | { kind: 'signups'; period: SpokenPeriod }
  /** "What refunds went out yesterday and why?" */
  | { kind: 'refunds'; period: SpokenPeriod }
  /** "Read me the cancellations." */
  | { kind: 'cancellations'; period: SpokenPeriod }
  /** "How many Runners are active right now?" */
  | { kind: 'runners-now' }
  /**
   * "Send order 1 2 3 4 to Tomasz", "send all waiting orders to Ayesha" (ruling 80): the numbers
   * are those the Allocate jobs view shows; `all` is every order still waiting for a Runner.
   */
  | { kind: 'allocate'; orders: number[] | 'all'; runner: string }
  /** "Unlock Yvette" (ruling 81): whatever that person is locked out of. */
  | { kind: 'unlock'; name: string }
  /** "Reset Yvette's PIN" (ruling 81): a Shopper chooses a new one the next time. */
  | { kind: 'reset-pin'; name: string }
  /**
   * "Do I have any shop partners I need to approve?" (ruling 86): new partner shops waiting for
   * approval, never their products, which are the 'partners' list.
   */
  | { kind: 'partner-shops' };

/** Partner shops: "shop partner(s)", "partner shop(s)", "partner(s)", "new shop(s)", "shops". */
const PARTNER_SHOP_WORDS = /\b(shop ?partners?|partner ?shops?|partners?|new shops?|shops)\b/;
/** Their products, a different thing: "product(s)", "shop product(s)". */
const PRODUCT_WORDS = /\bproducts?\b/;

/**
 * What was said with any "I don't mean X" taken out (ruling 86), so "I do not mean shop
 * products. I am saying shop partner to approve." is about shop partners.
 */
export function withoutCorrection(said: string): string {
  return said
    .toLowerCase()
    .replace(/’/g, "'")
    .replace(
      /\b(?:i )?(?:do not|don'?t|did not|didn'?t|am not|wasn'?t|was not) (?:mean|meaning|asking about|talking about|saying)\b[^.?!;]*?(?=[.?!;]|\b(?:i mean|i'?m saying|i am saying|i'?m asking|i am asking|but)\b|$)/g,
      ' ',
    );
}

/** Whether what was said is about new partner shops rather than shop products (ruling 86). */
export function aboutPartnerShops(said: string): boolean {
  const text = withoutCorrection(said).replace(/[?.!,;]+/g, ' ');
  return PARTNER_SHOP_WORDS.test(text) && !PRODUCT_WORDS.test(text);
}

/**
 * "unlock Yvette", "please unlock Kemi", "reset Yvette's PIN", "reset the PIN for Yvette"
 * (ruling 81). Null when it is not that. "Unlock the screen" and the like are not names.
 */
export function unlockCommand(text: string): StaffCommand | null {
  const said = text
    .toLowerCase()
    .replace(/[?.!,]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(please |ozi |can you |could you )+/, '')
    .replace(/\s+please$/, '');
  const reset =
    said.match(/^reset (.+?)(?:'s|s'|’s)? pin(?: number)?$/) ??
    said.match(/^reset (?:the )?pin (?:for|of) (.+)$/);
  if (reset) {
    const name = (reset[1] ?? '').replace(/^(the )?(shopper |customer )/, '').trim();
    return name === '' || name === 'my' || name === 'the' ? null : { kind: 'reset-pin', name };
  }
  const unlock = said.match(/^unlock (.+)$/);
  if (!unlock) return null;
  const name = (unlock[1] ?? '')
    .replace(/^(the )?(shopper|customer|runner|staff member) /, '')
    .trim();
  if (name === '' || /^(it|the (screen|phone|door)|my|me|everyone|everybody)$/.test(name))
    return null;
  return { kind: 'unlock', name };
}

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  won: 1,
  two: 2,
  too: 2,
  three: 3,
  four: 4,
  for: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  ate: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
};

/**
 * "send order 1 2 3 4 to Tomasz", "give orders one, two and three to Ayesha", "send all waiting
 * orders to Bola", "allocate jobs 5 and 6 to Kemi". Null when it is not that. The name is the
 * words after the last "to".
 */
export function allocateCommand(text: string): StaffCommand | null {
  const said = text
    .toLowerCase()
    .replace(/[?.!,]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(please |ozi |can you |could you )+/, '');
  const match = said.match(/^(?:send|give|allocate|pass)\s+(.+)\s+to\s+(.+?)(?:\s+please)?$/);
  if (!match) return null;
  const what = match[1] ?? '';
  const runner = (match[2] ?? '').replace(/^(runner )/, '').trim();
  if (!/\b(orders?|jobs?)\b/.test(what) || runner === '') return null;
  if (/\ball\b/.test(what)) return { kind: 'allocate', orders: 'all', runner };
  const numbers: number[] = [];
  for (const word of what.split(/\s+|-/)) {
    if (/^\d+$/.test(word)) numbers.push(Number(word));
    else if (NUMBER_WORDS[word] !== undefined) numbers.push(NUMBER_WORDS[word]);
  }
  if (numbers.length === 0) return null;
  return { kind: 'allocate', orders: numbers, runner };
}

/**
 * The order numbers meant: a run of digits heard as one number ("1234") larger than the list is
 * taken digit by digit when every digit is on it. Duplicates are dropped, the order kept.
 */
export function orderNumbersMeant(numbers: readonly number[], count: number): number[] {
  const out: number[] = [];
  for (const number of numbers) {
    const digits = String(number).split('').map(Number);
    const parts =
      number > count && digits.length > 1 && digits.every((digit) => digit >= 1 && digit <= count)
        ? digits
        : [number];
    for (const part of parts) if (!out.includes(part)) out.push(part);
  }
  return out;
}

/** A stretch of time, as people say it. */
export type SpokenPeriod =
  'today' | 'yesterday' | 'week' | 'last-week' | 'seven-days' | 'month' | 'last-month' | 'year';

/** "this month", "yesterday", "last week": null when nothing like it was said. */
export function spokenPeriod(text: string): SpokenPeriod | null {
  const said = text.toLowerCase();
  if (/\btoday\b/.test(said)) return 'today';
  if (/\byesterday\b/.test(said)) return 'yesterday';
  if (/\blast week\b/.test(said)) return 'last-week';
  if (/\b(last|past) (7|seven) days\b|\bpast week\b/.test(said)) return 'seven-days';
  if (/\bthis week\b/.test(said)) return 'week';
  if (/\blast month\b/.test(said)) return 'last-month';
  if (/\b(this|the) month\b|\bpast month\b/.test(said)) return 'month';
  if (/\b(this|the) year\b/.test(said)) return 'year';
  return null;
}

/** The two moments a period runs between, on this device's own clock, and how it is said. */
export function periodRange(
  period: SpokenPeriod,
  now = new Date(),
): { from: Date; to: Date; words: string } {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = 24 * 60 * 60 * 1000;
  const monday = new Date(midnight.getTime() - ((midnight.getDay() + 6) % 7) * day);
  const soon = new Date(now.getTime() + 1000);
  switch (period) {
    case 'today':
      return { from: midnight, to: soon, words: 'Today' };
    case 'yesterday':
      return { from: new Date(midnight.getTime() - day), to: midnight, words: 'Yesterday' };
    case 'week':
      return { from: monday, to: soon, words: 'This week' };
    case 'last-week':
      return { from: new Date(monday.getTime() - 7 * day), to: monday, words: 'Last week' };
    case 'seven-days':
      return { from: new Date(now.getTime() - 7 * day), to: soon, words: 'In the last seven days' };
    case 'month':
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1),
        to: soon,
        words: 'This month',
      };
    case 'last-month':
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        to: new Date(now.getFullYear(), now.getMonth(), 1),
        words: 'Last month',
      };
    case 'year':
      return { from: new Date(now.getFullYear(), 0, 1), to: soon, words: 'This year' };
  }
}

const AREA_PATTERNS: Array<[StaffArea, RegExp]> = [
  ['audit', /\b(audit( log)?|who did what)\b/],
  ['accounts', /\b(shopper accounts?|open (a |the )?shopper'?s? account)\b/],
  ['orders', /\b(live orders|the orders|orders)\b/],
  ['reports', /\b(reports?|price freshness|how fresh)\b/],
  ['payments', /\b(payments?|bank transfers?|transfers?)\b/],
  [
    'money',
    /\b(money|income|takings|stripe|revenue|earnings|paid in|how much (came|have we taken))\b/,
  ],
  [
    'overview',
    /\b(overview|needs? (me|you|us) now|what needs me|how many (shoppers|runners|people|staff)|head ?count)\b/,
  ],
  ['analytics', /\b(analytics|analysis|numbers|statistics|stats|insights?|trends?)\b/],
  ['problems', /\b(complaints?|problems?|issues?|disputes?|refunds?)\b/],
  ['feedback', /\bfeedback\b/],
  ['documents', /\b(documents?|dbs|right to work|share codes?|insurance|licen[cs]es?|checks)\b/],
  ['owed', /\b(money owed|owed|owes|debts?|write[ -]?offs?)\b/],
  ['finds', /\b(finds? it|finds|searches|looking for)\b/],
  ['enquiries', /\b(enquir(y|ies)|inquir(y|ies)|messages?|emails?|web ?mail|organisations?)\b/],
  // Shop products waiting to be checked. New partner shops are 'partner-shops' (ruling 86).
  ['partners', /\b(shop products?|products?)\b/],
  ['team', /\b(team|staff|colleagues?)\b/],
  [
    'learning',
    /\b(learning|unanswered|questions? (ozi|you) (could ?n.?t|couldn't|did ?n.?t) answer)\b/,
  ],
];

/** "2 pounds 50", "£2.50", "250 pence", "five pounds". */
export function spokenMoney(said: string): number | null {
  const words: Record<string, number> = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    fifteen: 15,
    twenty: 20,
    thirty: 30,
    forty: 40,
    fifty: 50,
  };
  const text = said
    .toLowerCase()
    .replace(
      /\b(one|two|three|four|five|six|seven|eight|nine|ten|fifteen|twenty|thirty|forty|fifty)\b/g,
      (word) => String(words[word]),
    );
  const pounds =
    text.match(/£\s*(\d+)(?:\.(\d{1,2}))?/) ??
    text.match(
      /(\d+)(?:\.(\d{1,2}))?\s*(?:pounds?|quid)(?:\s*(?:and\s*)?(\d{1,2})\s*(?:p|pence)?)?/,
    );
  if (pounds) {
    const whole = Number(pounds[1]);
    const part = pounds[2] ?? pounds[3];
    const pence = part === undefined ? 0 : Number(part.length === 1 ? `${part}0` : part);
    return whole * 100 + pence;
  }
  const pence = text.match(/(\d+)\s*(?:p|pence)\b/);
  return pence ? Number(pence[1]) : null;
}

export function understand(said: string): StaffCommand | null {
  const text = said
    .toLowerCase()
    .replace(/[?.!,]+/g, ' ')
    .trim();
  if (/\b(sign me out|sign out|log out|log me out)\b/.test(text)) return { kind: 'sign-out' };
  // Sending jobs straight to a Runner (ruling 80), before anything that "next" or "back" catches.
  const allocate = allocateCommand(said);
  if (allocate) return allocate;
  // Unlocking someone, or resetting a Shopper's PIN (ruling 81).
  const unlock = unlockCommand(said);
  if (unlock) return unlock;
  // New partner shops waiting for approval, never their products (ruling 86), before "approve"
  // and the lists: "Do I have any shop partners I need to approve?"
  if (aboutPartnerShops(said)) return { kind: 'partner-shops' };
  // "I don't mean partner shops, I mean products": the products, whatever else was said.
  if (
    /\b(?:i )?(?:do not|don'?t|did not|didn'?t) mean\b/.test(text) &&
    PRODUCT_WORDS.test(withoutCorrection(said))
  ) {
    return { kind: 'open', area: 'partners' };
  }
  if (/\b(help|what can (you|i) (do|say))\b/.test(text)) return { kind: 'help' };
  if (/\b(what'?s waiting|summary|anything new|what'?s new|catch me up|overview)\b/.test(text)) {
    return { kind: 'summary' };
  }
  // The owner's questions about the business (Section Q): numbers, totals and patterns only.
  if (/\b(sign ?-?ups?|signed up|new (shoppers|runners|accounts)|joined)\b/.test(text)) {
    return { kind: 'signups', period: spokenPeriod(text) ?? 'month' };
  }
  if (/\brefunds\b|\brefunds? (went|go|gone|given|issued|sent)\b/.test(text)) {
    return { kind: 'refunds', period: spokenPeriod(text) ?? 'seven-days' };
  }
  if (/\bcancell?ations?\b|\bcancell?ed( orders)?\b/.test(text)) {
    return { kind: 'cancellations', period: spokenPeriod(text) ?? 'seven-days' };
  }
  if (
    /\b(runners? (are |is )?(active|working|on shift|out)|active runners|who'?s (working|on shift)|runners (right )?now|read me the runners)\b/.test(
      text,
    )
  ) {
    return { kind: 'runners-now' };
  }
  if (/\b(rung (them )?back|called (them )?back|done|handled|i'?ve rung)\b/.test(text))
    return { kind: 'rung-back' };
  if (/\b(next|skip|carry on|the next one)\b/.test(text)) return { kind: 'next' };
  if (/\b(previous|back|go back|last one|the one before)\b/.test(text)) return { kind: 'previous' };
  if (/\b(read (it|that) again|again|repeat that)\b/.test(text)) return { kind: 'again' };

  const decisions: Array<[ProblemDecision, RegExp]> = [
    ['no_fault', /\b(nobody|no one|no-one|noone)\b.*\bfault\b|\bno fault\b/],
    ['runner_at_fault', /\b(the )?runner\b.*\b(fault|responsible)\b/],
    ['shop_at_fault', /\b(the )?shop\b.*\b(fault|responsible)\b/],
    ['platform_at_fault', /\b(our|we were|we are|the platform'?s?)\b.*\b(fault|responsible)\b/],
    ['shopper_at_fault', /\b(the )?(shopper|customer)\b.*\b(fault|responsible)\b/],
  ];
  for (const [decision, pattern] of decisions) {
    if (pattern.test(text)) {
      return {
        kind: 'decide',
        decision,
        refundPence: decision === 'shopper_at_fault' ? 0 : spokenMoney(text),
      };
    }
  }
  if (/\b(write (it|that) off|write off)\b/.test(text)) return { kind: 'write-off' };
  if (/\b(not found|couldn'?t find|no luck|none of the shops)\b/.test(text))
    return { kind: 'not-found' };
  if (/\b(found it|we found|i found)\b/.test(text)) return { kind: 'found' };
  if (/\b(turn (it|that) down|reject|refuse|decline)\b/.test(text)) return { kind: 'reject' };
  if (/\b(accept|approve|that'?s fine|looks good)\b/.test(text)) return { kind: 'accept' };

  for (const [area, pattern] of AREA_PATTERNS) {
    if (pattern.test(text)) return { kind: 'open', area };
  }
  return null;
}

/** What Ozi can do here, for this job. */
export function help(areas: readonly StaffArea[], canUnlock = false): string {
  const tabs = areas.map((area) => AREA_WORDS[area].tab.toLowerCase());
  return (
    `You can say: "what's waiting", to hear everything; "read me the" and then ${listWords(tabs)}; ` +
    '"next", "back", or "again", to move through a list; and the decision itself, such as "accept", ' +
    '"nobody at fault, refund 2 pounds 50", "write it off", "not found" or "rung back". ' +
    (areas.includes('reports')
      ? 'You can ask "how many signups this month", "what refunds went out yesterday and why" or "read me the cancellations". '
      : '') +
    (areas.includes('orders')
      ? 'If you may allocate jobs, say "send order 1 2 3 to" and a Runner\'s name, or "send all waiting orders to" and a name. '
      : '') +
    (canUnlock
      ? 'To let someone back in, say "unlock" and their name, or "reset" and a Shopper\'s name and "PIN". '
      : '') +
    'I always read a decision back and wait for your yes. Say "sign me out" when you finish.'
  );
}

/** The business numbers, read aloud: no names, groups of ten or more only. */
export function analyticsWords(data: StaffAnalytics): string {
  const t = data.totals;
  const busiest = data.shops[0];
  const hour = [...data.hours].sort((a, b) => b.purchases - a.purchases)[0];
  const day = [...data.weekdays].sort((a, b) => b.purchases - a.purchases)[0];
  const unmet = data.unmetSearches.slice(0, 3).map((row) => row.term);
  const parts = [
    `In the last ${data.period}: ${t.purchases} purchase${t.purchases === 1 ? '' : 's'}, worth ${money(t.goodsPence)} of shopping` +
      (t.shoppers !== null ? `, from ${t.shoppers} Shoppers` : '') +
      `, delivered by ${t.runners} Runner${t.runners === 1 ? '' : 's'}.`,
  ];
  if (busiest) parts.push(`Busiest shop: ${busiest.shop}, with ${busiest.purchases}.`);
  if (hour && hour.purchases > 0) parts.push(`Busiest hour: ${hour.hour} o'clock.`);
  if (day && day.purchases > 0) parts.push(`Busiest day: ${day.day}.`);
  if (data.ageBands.rows[0])
    parts.push(
      `Most purchases are by the ${data.ageBands.rows[0].key.replace('_', ' to ').replace('plus', 'and over')} age group.`,
    );
  if (unmet.length > 0) parts.push(`Most wanted, and nobody has it: ${listWords(unmet)}.`);
  if (t.throughOrganisations > 0)
    parts.push(`${t.throughOrganisations} came through organisations.`);
  return parts.join(' ');
}

/** The first screen, read aloud: what needs a person now, then people and work, no money. */
export function overviewWords(data: StaffOverview): string {
  const p = data.people;
  const w = data.work;
  return (
    (data.needsYou ? `${needsYouWords(data.needsYou)} ` : '') +
    `${p.shoppers} Shoppers, ${p.runners} Runners, ${p.runnersOnShiftNow} on shift now, ` +
    `${p.shopPartners} Shop Partners, ${p.organisations} organisations and ${p.staff} staff. ` +
    `${w.ordersLastDay} orders in the last day, ${w.ordersLastWeek} in the last week. Waiting: ` +
    `${w.problemsWaiting} problems to decide, ${w.documentsWaiting} Runner documents, ` +
    (w.partnerShopsWaiting !== undefined
      ? `${w.partnerShopsWaiting} new shop ${w.partnerShopsWaiting === 1 ? 'partner' : 'partners'} waiting for approval, `
      : '') +
    `${w.shopProductsWaiting} shop products to check and ${w.findItWaiting} Finds It requests.`
  );
}

/**
 * New partner shops waiting for approval, by name (ruling 86): "You have 2 new shop partners
 * waiting for approval: Corner Shop and Bakery." Never their products.
 */
export function partnerShopsWords(count: number, names: readonly string[]): string {
  if (count === 0) return 'No new shop partners are waiting for approval.';
  const shown = names.slice(0, 5);
  const more = count - shown.length;
  const which =
    shown.length === 0 ? '' : `: ${listWords(more > 0 ? [...shown, `${more} more`] : [...shown])}`;
  return `You have ${count} new shop ${count === 1 ? 'partner' : 'partners'} waiting for approval${which}. They are on the Shops and organisations tab.`;
}

/**
 * For the owner, what the admin panel still answers by itself (ruling 86): everything else he
 * says goes to his conversation first, and comes back here only when that is away, slow or
 * silent. Kept here: signing out, explicit moves ("open the documents tab", "go to orders",
 * "read me the complaints"), short moves and decisions on the item being read to him, and the
 * commands with their own read-back that the conversation cannot carry out (sending jobs to a
 * Runner, unlocking, resetting a PIN). His questions, requests and statements are not.
 */
export function ownerAnswersHere(
  command: StaffCommand | null,
  said: string,
  readingItem: boolean,
): boolean {
  if (!command) return false;
  const text = said
    .toLowerCase()
    .replace(/[?.!,]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(ozi |please )+/, '')
    .replace(/ please$/, '');
  const short = text.split(' ').length <= 8;
  switch (command.kind) {
    case 'sign-out':
    case 'allocate':
    case 'unlock':
    case 'reset-pin':
      return true;
    case 'help':
      return /^(help|what can (you|i) (do|say))$/.test(text);
    case 'open':
    case 'partner-shops':
    case 'runners-now':
    case 'cancellations':
      // An explicit move, not a question: "open the documents tab", "go to orders".
      return (
        short &&
        !text.includes(' mean') &&
        /^(open|go to|show( me)?|take me to|switch to|read( me)?|read out)( the| my)? \S/.test(text)
      );
    case 'next':
    case 'previous':
    case 'again':
      return readingItem && short;
    case 'decide':
    case 'accept':
    case 'reject':
    case 'write-off':
    case 'not-found':
    case 'found':
    case 'rung-back':
      return readingItem && short;
    case 'summary':
    case 'signups':
    case 'refunds':
      return false;
  }
}

/** The owner's money, read aloud: today and the last month, by gateway. */
export function moneyWords(data: OwnerMoney): string {
  const gateways = data.month.byGateway.map((row) => `${row.name}, ${money(row.pence)}`);
  return (
    `Today, ${money(data.today.inPence)} in and ${money(data.today.outPence)} given back. ` +
    `In the last month, ${money(data.month.inPence)} in, ${money(data.month.outPence)} back, ` +
    `${money(data.month.netPence)} kept.` +
    (gateways.length ? ` By gateway: ${listWords(gateways)}.` : '')
  );
}

function times(count: number): string {
  return count === 1 ? 'once' : count === 2 ? 'twice' : `${count} times`;
}

/** "The Shopper cancelled before paying." as "the Shopper cancelled before paying". */
function clause(reason: string): string {
  const trimmed = reason.trim().replace(/[.!]+$/, '');
  return /^(The|A|An|No) /.test(trimmed) ? trimmed[0]!.toLowerCase() + trimmed.slice(1) : trimmed;
}

/** "How many signups this month?": numbers only, no names. */
export function signupsWords(when: string, shoppers: number, runners: number): string {
  if (shoppers === 0 && runners === 0) return `${when}, nobody has signed up.`;
  const parts = [
    `${shoppers} ${shoppers === 1 ? 'Shopper' : 'Shoppers'}`,
    `${runners} ${runners === 1 ? 'Runner' : 'Runners'}`,
  ];
  return `${when}, ${listWords(parts)} signed up.`;
}

/**
 * "What refunds went out yesterday and why?": how many, why in groups, and the total only for
 * the owner (the server leaves it out for anyone else). Never a name.
 */
export function refundsWords(when: string, report: RefundsReport): string {
  if (report.count === 0) return `${when}, no refunds went out.`;
  const groups = report.byReason.map((row) => `${row.count} for ${row.reason}`);
  return (
    `${when}, ${report.count} ${report.count === 1 ? 'refund' : 'refunds'} went out: ${listWords(groups)}.` +
    (report.totalPence !== null ? ` ${money(report.totalPence)} altogether.` : '')
  );
}

/** "Read me the cancellations": how many, and why, in patterns. Never a name. */
export function cancellationsWords(when: string, report: CancellationsReport): string {
  if (report.count === 0) return `${when}, no orders were cancelled.`;
  const reasons = report.byReason.map((row) => `${times(row.count)}, ${clause(row.reason)}`);
  const why = listWords(reasons);
  return `${when}, ${report.count} ${report.count === 1 ? 'order was' : 'orders were'} cancelled. ${why.charAt(0).toUpperCase()}${why.slice(1)}.`;
}

/** "How many Runners are active right now?" */
export function runnersNowWords(data: RunnersNow): string {
  if (data.activeNow === 0) return 'No Runners are active right now.';
  return `${data.activeNow} ${data.activeNow === 1 ? 'Runner is' : 'Runners are'} active right now: ${data.onShift} on shift and ${data.onAJob} on a job.`;
}

/** What Ozi says first when the owner asks for something (ruling 44), taken in turn. */
export const OWNER_ACKNOWLEDGEMENTS = ['Yes, sir.', 'Okay, sir.', 'All right, sir.'];

/**
 * Words for the owner (Anthony, 7 October 2026: "yes sir, okay sir, all right sir", with sir at
 * the end): "sir" at the end of what is said, unless it is there already, and an
 * acknowledgement first when he has asked for something.
 */
export function toOwner(text: string, acknowledgement: string | null = null): string {
  const ended = /\bsir\b/i.test(text)
    ? text
    : text.replace(/\s*([.!?])?\s*$/, (_whole, stop: string | undefined) => `, sir${stop ?? '.'}`);
  if (!acknowledgement || /^(yes|okay|all right), sir\b/i.test(ended)) return ended;
  return `${acknowledgement} ${ended}`;
}
